import { formatClock } from './time';
import type { SlotConflictItem } from './types';

/**
 * Rendering for the API's canonical slot-occupancy record (givip/prospin-api#81,
 * extended to the series-create 409 by api#121).
 *
 * Lives in `lib/` rather than beside one banner because two components render
 * the same wire object — `SlotConflictBanner` (single window) and
 * `SeriesConflictsBanner` (a projection over many dates). One shape gets one
 * rendering; a second copy is how the two would drift apart again.
 */

const KIND_LABELS: Record<SlotConflictItem['kind'], string> = {
  booking: 'Booking',
  coach_block: 'Coach session',
  hold: 'Unpaid hold',
};

/**
 * One occupied interval as a single line.
 *
 * Built by filtering out absent parts and joining, rather than interpolating
 * them: `owner_name` is omitted entirely on non-admin bodies and `booking_id`
 * is null for a payment hold, so a template string would print "undefined" or
 * leave a dangling separator.
 *
 * Falls back to the raw `kind` for an unknown value — the API may add a kind
 * before this client knows about it, and showing the wire value beats showing
 * "undefined".
 */
export function occupancyLine(c: SlotConflictItem): string {
  const kind = KIND_LABELS[c.kind] ?? c.kind;
  return [
    `${formatClock(c.start_time)}–${formatClock(c.end_time)}`,
    kind,
    c.owner_name || null,
    c.booking_id != null ? `#${c.booking_id}` : null,
  ]
    .filter((part): part is string => Boolean(part))
    .join(' · ');
}

/**
 * The "when does this table free up" line, or null if there is nothing to say.
 *
 * Three wire states, one rule for both banners:
 *   - key absent  → null. An API predating api#121 simply doesn't report this;
 *     claiming the table is fully booked on that evidence would be wrong.
 *   - key null    → it never frees up that day, stated plainly.
 *   - a time      → that time.
 *
 * Each branch tests the wire value rather than its truthiness, so a blank
 * string — not something prospin-api sends, but the reason for testing the
 * value is that we don't get to assume that — falls through to null instead of
 * either asserting the table is full or printing "Next free …: " with a hole
 * where the time should be.
 *
 * `onDate` qualifies the claim for callers whose payload spans several dates (a
 * series projection): the API returns one scalar, scoped to the earliest
 * conflicting date, so an unqualified sentence would read as if it held for all
 * of them. Omit it when the payload covers a single date.
 */
export function nextFreeLine(
  nextFreeTime: string | null | undefined,
  onDate?: string,
): string | null {
  if (nextFreeTime === undefined) return null;
  // "that day" only reads right when no date was named; with one it is redundant.
  const where = onDate ? `this table on ${onDate}` : 'this table';
  const noneSuffix = onDate ? '' : ' that day';
  if (nextFreeTime === null) return `No free time left on ${where}${noneSuffix}.`;
  if (!nextFreeTime.trim()) return null;
  return `Next free on ${where}: ${formatClock(nextFreeTime)}`;
}
